import { fetchLockedReferrers } from "../gql/records.js"
import { fetchSchemaLockedBy } from "../gql/schemas.js"
import type { Mutate, Query } from "../index.js"
import type { createRecordsService } from "./records.js"

/** Postgres refuses a write to a locked record (check_violation). Hasura passes the
 *  RAISE text through as the GraphQL error's message, possibly prefixed, so the
 *  message is what is matched. A locked record's message quotes its lock reason
 *  in parentheses. */
const LOCKED = /Record locked: record (\d+) is locked(?: \((.*?)\))?\. Unlock it in Auger/
const AGENT = /Record lock: only a person in Auger can lock or unlock a record\./
const COMBINED = /Record lock: change the lock on its own, without other changes\./
/** The schema guard (guard_locked_schema): the schema, one locked list that uses
 *  it, and that list's reason, quoted. */
const SCHEMA = /Schema locked: schema (\d+) is used by locked list (\d+) \(reason: "(.*?)"\)\. Unlock the list in Auger/

/** The write touched a locked record. Unlock it in Auger to change it. `reason`
 *  is the record's lock reason, when the message carries it. */
export class RecordLockedError extends Error {
  readonly recordId: number | null
  readonly reason: string | null
  /** Other changes of the same write were saved before the lock refused the
   *  rest (see relabelLockError). */
  readonly partialWrite: boolean
  constructor(
    message: string,
    recordId: number | null,
    reason: string | null = null,
    options?: ErrorOptions & { partialWrite?: boolean }
  ) {
    super(message, options)
    this.name = "RecordLockedError"
    this.recordId = recordId
    this.reason = reason
    this.partialWrite = options?.partialWrite ?? false
  }
}

/** An agent (not a person in Auger) tried to lock or unlock a record. */
export class RecordLockAgentError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = "RecordLockAgentError"
  }
}

/** A write changed the lock and other fields together. */
export class RecordLockCombinedError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = "RecordLockCombinedError"
  }
}

export type LockError =
  | RecordLockedError
  | RecordLockAgentError
  | RecordLockCombinedError
  | SchemaLockedError

export const isLockError = (err: unknown): err is LockError =>
  err instanceof RecordLockedError ||
  err instanceof RecordLockAgentError ||
  err instanceof RecordLockCombinedError ||
  err instanceof SchemaLockedError

/** The typed lock error when `err` carries one of the guard messages, else
 *  null. Reads the GraphQL errors from both transports (Apollo's `graphQLErrors`
 *  and a plain `errors` array) and the error's own message. The typed error's
 *  `cause` is `err`. */
export function lockError(err: unknown): LockError | null {
  if (isLockError(err)) return err
  const e = err as {
    graphQLErrors?: Array<{ message?: string }>
    errors?: Array<{ message?: string }>
    message?: string
  } | null
  const messages = [
    ...(e?.graphQLErrors ?? []).map((x) => x?.message),
    ...(e?.errors ?? []).map((x) => x?.message),
    e?.message,
  ]
  const options = { cause: err }
  for (const message of messages) {
    if (typeof message !== "string") continue
    const locked = LOCKED.exec(message)
    if (locked) return new RecordLockedError(message, Number(locked[1]), locked[2] ?? null, options)
    if (AGENT.test(message)) return new RecordLockAgentError(message, options)
    if (COMBINED.test(message)) return new RecordLockCombinedError(message, options)
    // The database names the list by id only; the name is that id.
    const schema = SCHEMA.exec(message)
    if (schema) {
      const list = { id: Number(schema[2]), name: `#${schema[2]}`, reason: schema[3] || null }
      return new SchemaLockedError(Number(schema[1]), [list], options)
    }
  }
  return null
}

/** The same typed lock error with a new `message`, its `cause` the original: for a
 *  caller that adds context to a lock refusal and keeps its class. `partialWrite`
 *  says other changes of the write were already saved (a RecordLockedError
 *  carries it, for an interface to say so). */
export function relabelLockError(
  err: LockError,
  message: string,
  { partialWrite = false }: { partialWrite?: boolean } = {}
): LockError {
  const options = { cause: err }
  if (err instanceof RecordLockedError)
    return new RecordLockedError(message, err.recordId, err.reason, { ...options, partialWrite })
  if (err instanceof RecordLockAgentError) return new RecordLockAgentError(message, options)
  if (err instanceof SchemaLockedError) {
    const relabelled = new SchemaLockedError(err.schemaId, err.lists, options)
    relabelled.message = message
    return relabelled
  }
  return new RecordLockCombinedError(message, options)
}

/** `mutate` that rethrows a guard refusal as its typed error, so every write built
 *  on it reports a lock the same way. */
export function withLockErrors(mutate: Mutate): Mutate {
  return async (...args) => {
    try {
      return await mutate(...args)
    } catch (err) {
      throw lockError(err) ?? err
    }
  }
}

/** A locked list that uses a schema. */
export interface SchemaLockedList {
  id: number
  name: string
  reason: string | null
}

/** The schema belongs to a locked list, so it is not changed or deleted. Core
 *  asks first (assertSchemaUnlocked) and names every locked list that uses it;
 *  the database guard (guard_locked_schema) refuses a write that skips core, and
 *  lockError maps its message to this error with the one list it names. */
export class SchemaLockedError extends Error {
  readonly schemaId: number
  readonly lists: SchemaLockedList[]
  constructor(schemaId: number, lists: SchemaLockedList[], options?: ErrorOptions) {
    const more = lists.length > 1 ? `, and ${lists.length - 1} more` : ""
    super(
      `Schema ${schemaId} is used by locked list "${lists[0]?.name}"${more}. Unlock the list to change it.`,
      options
    )
    this.name = "SchemaLockedError"
    this.schemaId = schemaId
    this.lists = lists
  }
}

/** Locked lists that use the schema, by name: through `schema_id` (their
 *  columns) or `data.listing_schema_id` (their per-entry fields). */
export async function schemaLockedBy(query: Query, schemaId: number): Promise<SchemaLockedList[]> {
  const data = await query(fetchSchemaLockedBy, { id: schemaId })
  const seen = new Set<number>()
  const lists: SchemaLockedList[] = []
  for (const l of data.lists) {
    if (seen.has(l.id)) continue
    seen.add(l.id)
    lists.push({ id: l.id, name: l.name, reason: l.lock_reason ?? null })
  }
  return lists
}

/** Throws `SchemaLockedError` when a locked list uses the schema. */
export async function assertSchemaUnlocked(query: Query, schemaId: number): Promise<void> {
  const lists = await schemaLockedBy(query, schemaId)
  if (lists.length) throw new SchemaLockedError(schemaId, lists)
}

/** records.lock_reason's limit (records_lock_reason_check), in characters. */
export const MAX_LOCK_REASON = 200

// The database's [[:cntrl:]]: C0, DEL and C1.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/

/** A locked record that a record's delete or merge would change, by how:
 *  - `list`: a locked list lists the record (the listing is the list's row);
 *  - `link`: a locked record links to it (the link is that record's row);
 *  - `child`: a locked record is its child (a delete sets the child's
 *    `parent_id` to NULL; a merge moves it);
 *  - `owner`: the record is a person who owns a locked record (a merge moves
 *    its `owner_person_id`). */
export type LockedReferrer =
  | { via: "list"; id: number; name: string }
  | { via: "link"; id: number; name: string }
  | { via: "child"; id: number; name: string }
  | { via: "owner"; id: number; name: string }

/** The order `locks.referrers` returns: by `via` in this order, then by name. */
const VIA_ORDER: ReadonlyArray<LockedReferrer["via"]> = ["list", "link", "child", "owner"]

export function createLocksService({
  query,
  records,
}: {
  query: Query
  records: Pick<ReturnType<typeof createRecordsService>, "update">
}) {
  return {
    /** Lock a record, saying why. Only the lock goes in the write: the guard
     *  refuses it combined with other changes. A reason the database would
     *  refuse (empty, longer than MAX_LOCK_REASON, or with a control character)
     *  is refused here, before any write. */
    async lock(recordId: number, reason: string): Promise<void> {
      const lockReason = reason.trim()
      if (!lockReason) throw new Error("locks.lock: a lock needs a reason")
      if ([...lockReason].length > MAX_LOCK_REASON)
        throw new Error(`locks.lock: a lock reason is at most ${MAX_LOCK_REASON} characters`)
      if (CONTROL.test(lockReason))
        throw new Error("locks.lock: a lock reason is one line, with no control characters")
      await records.update(recordId, { locked: true, lock_reason: lockReason })
    },

    /** Unlock a record and clear its reason, in a write that changes nothing else. */
    async unlock(recordId: number): Promise<void> {
      await records.update(recordId, { locked: false, lock_reason: null })
    },

    /** The locked records a delete or merge of the record would change: locked
     *  lists that list it, locked records that link to it, its locked children,
     *  and (for a person) the locked records it owns. While any exist the record
     *  cannot be deleted or merged. Each locked record comes once per `via`
     *  (two links from one record are one referrer), ordered by `via` (list,
     *  link, child, owner), then by name, then by id. */
    async referrers(recordId: number): Promise<LockedReferrer[]> {
      const data = await query(fetchLockedReferrers, { id: recordId })
      const all: LockedReferrer[] = [
        ...data.listings.map((l) => ({ via: "list" as const, id: l.list.id, name: l.list.name })),
        ...data.links.map((l) => ({ via: "link" as const, id: l.record.id, name: l.record.name })),
        ...data.children.map((r) => ({ via: "child" as const, id: r.id, name: r.name })),
        ...data.owned.map((r) => ({ via: "owner" as const, id: r.id, name: r.name })),
      ]
      const seen = new Set<string>()
      return all
        .filter((r) => {
          const key = `${r.via}:${r.id}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        .sort(
          (a, b) =>
            VIA_ORDER.indexOf(a.via) - VIA_ORDER.indexOf(b.via) ||
            a.name.localeCompare(b.name) ||
            a.id - b.id
        )
    },

    /** Locked lists that use the schema (see `schemaLockedBy`). */
    schemaLockedBy: (schemaId: number): Promise<SchemaLockedList[]> => schemaLockedBy(query, schemaId),
  }
}
